import {
  ApiResponse,
  BizApplication,
  EmailChangeData,
  FacebookPayload,
  IRestApiService,
  IUserApiCall,
  LoginCredentials,
  NewUser,
  NotificationsSettings,
  PasswordData,
  PaymentSettings,
  SaleStageConfig,
  User,
} from '../types';
import { TokenStorage } from '../infrastructure/TokenStorage';
import RestApiService from '../infrastructure/RestApiService';

class UserApiCall implements IUserApiCall {
  private restApiService: RestApiService;

  constructor(restApiService: RestApiService) {
    this.restApiService = restApiService;
  }

  login(credentials: LoginCredentials): Promise<ApiResponse> {
    return new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService.post('users/login', credentials).then(
        (data: ApiResponse) => {
          TokenStorage.setSession(data.token, data.refreshToken ?? null, 'users/refresh');
          resolve(data);
        },
        (err) => reject(err)
      );
    });
  }

  logout(): Promise<void> {
    const refreshToken = TokenStorage.getRefreshToken();
    TokenStorage.clearSession();
    TokenStorage.clearCMSession();
    if (refreshToken) {
      return this.restApiService.post('users/logout', { refreshToken }).catch(() => undefined);
    }
    return Promise.resolve();
  }

  addBillingTag(
    tag: string,
    singleVisitPrice: number,
    defaultPrice: number,
    duration: number,
    color: string,
    purpose: string,
    confirmPresence: boolean
  ): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService
        .post('users/add-tag', {
          tag,
          singleVisitPrice,
          defaultPrice,
          duration,
          color,
          purpose,
          confirmPresence,
        })
        .then(
          (data: ApiResponse) => {
            TokenStorage.updateAccessToken(data.token);
            resolve(data);
          },
          function (err) {
            reject(err);
          }
        );
    });
    return result;
  }

  updateBillingTag(
    tag: string,
    singleVisitPrice: number,
    defaultPrice: number,
    duration: number,
    color: string,
    purpose: string,
    confirmPresence: boolean
  ): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService
        .put('users/update-tag', {
          tag,
          singleVisitPrice,
          defaultPrice,
          duration,
          color,
          purpose,
          confirmPresence,
        })
        .then(
          (data: ApiResponse) => {
            TokenStorage.updateAccessToken(data.token);
            resolve(data);
          },
          function (err) {
            reject(err);
          }
        );
    });
    return result;
  }

  addSaleStage(config: SaleStageConfig): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService
        .post('users/add-sale-stage', {
          label: config.label,
        })
        .then(
          (data: ApiResponse) => {
            TokenStorage.updateAccessToken(data.token);
            resolve(data);
          },
          function (err) {
            reject(err);
          }
        );
    });
    return result;
  }

  updateSaleStage(index: number, config: SaleStageConfig): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService
        .put('users/update-sale-stage', {
          index,
          label: config.label,
          courseSignUpColumn: config.courseSignUpColumn,
          questionColumn: config.questionColumn,
          trialColumn: config.trialColumn,
          lowRetentionColumn: config.lowRetentionColumn,
        })
        .then(
          (data: ApiResponse) => {
            TokenStorage.updateAccessToken(data.token);
            resolve(data);
          },
          function (err) {
            reject(err);
          }
        );
    });
    return result;
  }

  updateNotifications(notifications: NotificationsSettings): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService.put('users/update-notifications', notifications).then(
        (data: ApiResponse) => {
          TokenStorage.updateAccessToken(data.token);
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
    return result;
  }

  updatePaymentSettings(data: PaymentSettings): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService.put('users/update-payment-settings', data).then(
        (data: ApiResponse) => {
          TokenStorage.updateAccessToken(data.token);
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
    return result;
  }

  deleteSaleStage(index: number): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService
        .post('users/delete-sale-stage', {
          index,
        })
        .then(
          (data: ApiResponse) => {
            TokenStorage.updateAccessToken(data.token);
            resolve(data);
          },
          function (err) {
            reject(err);
          }
        );
    });
    return result;
  }

  deleteBillingTag(tag: string): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService.post('users/delete-tag', { tag }).then(
        (data: ApiResponse) => {
          TokenStorage.updateAccessToken(data.token);
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
    return result;
  }

  async inShoes(email: string): Promise<ApiResponse> {
    // On first switch, persist the CM credentials so subsequent switches keep working
    if (!TokenStorage.getCMToken()) {
      TokenStorage.setCMSession(TokenStorage.getToken()!, TokenStorage.getRefreshToken());
    }
    const cmToken = await this._ensureValidCMToken();
    const data = await this.restApiService.postAs<ApiResponse>('users/inShoes/' + email, cmToken);
    // inShoes token — 8h only, no refresh, and tab-scoped (see setImpersonatedSession) since this
    // now always opens in a fresh tab rather than swapping the current tab's active session.
    TokenStorage.setImpersonatedSession(data.token);
    return data;
  }

  private async _ensureValidCMToken(): Promise<string> {
    const cmToken = TokenStorage.getCMToken()!;
    try {
      const payload = JSON.parse(atob(cmToken.split('.')[1]));
      if (payload.exp * 1000 - Date.now() > 60_000) {
        return cmToken;
      }
    } catch {
      // If decode fails, just try using the token as-is
      return cmToken;
    }

    const cmRefreshToken = TokenStorage.getCMRefreshToken();
    if (!cmRefreshToken) return cmToken;

    try {
      const refreshed = await this.restApiService.post<ApiResponse>('users/refresh', { refreshToken: cmRefreshToken });
      if (refreshed.token) {
        TokenStorage.setCMSession(refreshed.token, (refreshed as any).refreshToken ?? null);
        return refreshed.token;
      }
    } catch {
      // Refresh failed — fall back to the expired token and let the server reject
    }
    return cmToken;
  }

  loginBiz(credentials: LoginCredentials): Promise<ApiResponse> {
    return new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService.post('users/login-biz', credentials).then(
        (data: ApiResponse) => {
          TokenStorage.setSession(data.token, data.refreshToken ?? null, 'users/refresh');
          resolve(data);
        },
        (err) => reject(err)
      );
    });
  }

  facebook(fbPayload: FacebookPayload): Promise<ApiResponse> {
    const result = new Promise<ApiResponse>((resolve, reject) => {
      this.restApiService.post('users/facebook', fbPayload).then(
        (data: ApiResponse) => {
          TokenStorage.setTokenAndRotateLogin(data.token);
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
    return result;
  }

  fbLogin(token: string): void {
    TokenStorage.setTokenAndRotateLogin(token);
  }

  killSession(): void {
    TokenStorage.clearSession();
    TokenStorage.clearCMSession();
  }

  isTokenStillThere(): boolean {
    try {
      return localStorage.getItem('token') !== null;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  setLang(lang: string): void {
    try {
      localStorage.setItem('voopty-lang', lang);
    } catch (e) {
      console.error(e);
    }
  }

  getUserInfo(): Promise<User> {
    return this.restApiService.get('users');
  }

  getOwnerInfo(ownerId: string): Promise<User> {
    return this.restApiService.get(`users/${ownerId}`);
  }

  getPaymentSignatures(ownerId: string, paymentData: any): Promise<any> {
    return this.restApiService.post(`users/sign/${ownerId}`, paymentData);
  }

  signUp(newUser: NewUser): Promise<ApiResponse> {
    return this.restApiService.post('users', { user: newUser });
  }

  checkEmailExists(email: string): Promise<{ exists: boolean }> {
    return this.restApiService.get(`users/check-email?email=${encodeURIComponent(email)}`);
  }

  confirmUser(token: string): Promise<ApiResponse> {
    return this.restApiService.post('users/confirm/' + token);
  }

  resetPassword(data: PasswordData): Promise<ApiResponse> {
    return this.restApiService.post('users/resetPassword/', data);
  }

  forgetPassword(email: string): Promise<ApiResponse> {
    return this.restApiService.post('users/requestResetPassword/', {
      email,
    });
  }

  resendConfirmation(email: string): Promise<ApiResponse> {
    return this.restApiService.post('users/resend', { email });
  }

  updateUser(user: User): Promise<ApiResponse> {
    return this.restApiService.put('users', { user });
  }

  updateLiqPaySettings(data: { publicKey: string; privateKey: string; source: string; note: string }): Promise<any> {
    return this.restApiService.put('users/liqpay-settings', data);
  }

  updateWayForPaySettings(data: {
    merchantLogin: string;
    privateKey: string;
    source: string;
    note: string;
  }): Promise<any> {
    return this.restApiService.put('users/wayforpay-settings', data);
  }

  updateTelegramSettings(data: {
    telegramBotToken: string;
    telegramBot: string;
    telegramAdminLink: string;
  }): Promise<any> {
    return this.restApiService.put('users/telegram-settings', data);
  }

  resetGlobal(): Promise<any> {
    return this.restApiService.post('intern/reset-global', {});
  }

  disconnectLiqPay(): Promise<any> {
    return this.restApiService.delete('users/liqpay-settings');
  }

  disconnectWayForPay(): Promise<any> {
    return this.restApiService.delete('users/wayforpay-settings');
  }

  disconnectTelegram(): Promise<any> {
    return this.restApiService.delete('users/telegram-settings');
  }

  disconnectStripe(): Promise<any> {
    return this.restApiService.delete('users/stripe-settings');
  }

  updateMonobankAcquiringSettings(data: { token: string; note: string; source: string }): Promise<any> {
    return this.restApiService.put('users/monobank-acquiring-settings', data);
  }

  disconnectMonobankAcquiring(): Promise<any> {
    return this.restApiService.delete('users/monobank-acquiring-settings');
  }

  updateStripeSettings(data: { apiKey: string; note: string; source: string; webhookSecret: string }): Promise<any> {
    return this.restApiService.put('users/stripe-settings', data);
  }

  getPaymentIntegrationHistory(provider: string): Promise<any> {
    return this.restApiService.get(`users/payment-integration-history/${provider}`);
  }

  updatePassword(oldPassword: string, newPassword: string): Promise<ApiResponse> {
    return this.restApiService.put('users/pass', {
      oldPassword,
      newPassword,
    });
  }

  requestChangeEmail(newEmail: string): Promise<ApiResponse> {
    return this.restApiService.post('users/requestChangeEmail/', {
      newEmail,
    });
  }

  updateEmail(data: EmailChangeData): Promise<ApiResponse> {
    return this.restApiService.post('users/changeEmail/', data);
  }

  deleteMe(password: string): Promise<ApiResponse> {
    return this.restApiService.delete('users', { password });
  }

  applyBiz(application: BizApplication): Promise<ApiResponse> {
    return this.restApiService.post('users/apply', application);
  }

  genApiKey(lifetime: string): Promise<ApiResponse> {
    return this.restApiService.post('users/gen-api-key', {
      lifetime,
    });
  }

  getApiKey(): Promise<ApiResponse> {
    return this.restApiService.get('users/get-api-key');
  }

  getAllSettings(): Promise<ApiResponse> {
    return this.restApiService.get('users/get-all-settings');
  }

  updateMessagingEmailAlias(alias: string): Promise<{ ok: boolean; messagingEmailAlias: string | null }> {
    return this.restApiService.put('users/messaging-email-alias', { alias });
  }
}

export default UserApiCall;
